import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Row,
  Spinner,
} from "reactstrap"
import Select from "react-select"
import { toast } from "react-toastify"

import PageWrapper from "components/Common/PageWrapper"
import { formatUiDateTime } from "utils/formatDate"
import { useAuth } from "hooks/authHook"
import { ACCESS } from "constants/accessPermissions"
import {
  useAddLeadActivity,
  useAssignLead,
  useConvertLead,
  useLead,
  useLeadMeta,
  useUpdateLeadStatus,
} from "hooks/leadHook"

const STATUS_COLORS = {
  new: "secondary",
  contacted: "info",
  qualified: "primary",
  proposal: "warning",
  negotiation: "warning",
  won: "success",
  lost: "danger",
}

const titleCase = s =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase())

const ACTIVITY_TYPES = ["note", "call", "visit", "message"]

const LeadDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { hasPermission } = useAuth()

  const canManageStatus = hasPermission(ACCESS.LEAD.MANAGE_STATUS)
  const canAssign = hasPermission(ACCESS.LEAD.ASSIGN)
  const canConvert = hasPermission(ACCESS.LEAD.CONVERT)

  const { data: resp, isLoading } = useLead(id)
  const lead = resp?.data
  const { data: metaResp } = useLeadMeta()
  const meta = metaResp?.data ?? {}

  const statusMutation = useUpdateLeadStatus()
  const assignMutation = useAssignLead()
  const activityMutation = useAddLeadActivity()
  const convertMutation = useConvertLead()

  const [statusValue, setStatusValue] = useState(null)
  const [lostReason, setLostReason] = useState("")
  const [assignValue, setAssignValue] = useState(null)
  const [activityType, setActivityType] = useState("note")
  const [activityBody, setActivityBody] = useState("")
  const [followUpAt, setFollowUpAt] = useState("")
  const [convertName, setConvertName] = useState("")
  const [convertMobile, setConvertMobile] = useState("")

  if (isLoading || !lead) {
    return (
      <PageWrapper title="Lead" breadcrumbItem="Lead">
        <div className="text-center py-5">
          <Spinner color="primary" />
        </div>
      </PageWrapper>
    )
  }

  document.title = `Lead · ${lead.name}`

  const statusOptions = (meta.statuses ?? []).map(s => ({ value: s, label: titleCase(s) }))
  const assigneeOptions = (meta.assignees ?? []).map(e => ({ value: e.id, label: e.name }))
  const nextStatus = statusValue?.value ?? lead.status

  const runStatus = async () => {
    if (!statusValue) return
    try {
      await statusMutation.mutateAsync({
        id: lead.id,
        status: statusValue.value,
        lost_reason: statusValue.value === "lost" ? lostReason : undefined,
      })
      toast.success("Status updated")
      setStatusValue(null)
      setLostReason("")
    } catch (err) {
      toast.error(err?.response?.data?.message || "Update failed")
    }
  }

  const runAssign = async () => {
    if (!assignValue) return
    try {
      await assignMutation.mutateAsync({ id: lead.id, assigned_to: assignValue.value })
      toast.success("Lead assigned")
      setAssignValue(null)
    } catch (err) {
      toast.error(err?.response?.data?.message || "Assign failed")
    }
  }

  const runActivity = async () => {
    if (!activityBody && !followUpAt) return
    try {
      await activityMutation.mutateAsync({
        id: lead.id,
        type: activityType,
        body: activityBody || undefined,
        follow_up_at: followUpAt || undefined,
      })
      toast.success("Activity added")
      setActivityBody("")
      setFollowUpAt("")
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add activity")
    }
  }

  const runConvert = async () => {
    try {
      await convertMutation.mutateAsync({
        id: lead.id,
        name: convertName || lead.name,
        mobile: convertMobile || lead.mobile,
      })
      toast.success("Lead converted to customer")
    } catch (err) {
      toast.error(err?.response?.data?.message || "Convert failed")
    }
  }

  return (
    <PageWrapper title={`Lead · ${lead.name}`} breadcrumbItem="Lead" cardBody={false}>
      <Row>
        <Col lg={7}>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h4 className="mb-1">{lead.name}</h4>
                  <div className="text-muted">
                    {lead.luid} · {lead.mobile}
                    {lead.email ? ` · ${lead.email}` : ""}
                  </div>
                  <div className="d-flex gap-2 mt-2">
                    <a className="btn btn-sm btn-soft-primary" href={`tel:${lead.mobile}`}>
                      <i className="mdi mdi-phone" /> Call
                    </a>
                    {lead.whatsapp_number && (
                      <a
                        className="btn btn-sm btn-soft-success"
                        href={`https://wa.me/${lead.whatsapp_number}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <i className="mdi mdi-whatsapp" /> WhatsApp
                      </a>
                    )}
                  </div>
                </div>
                <Badge color={STATUS_COLORS[lead.status] ?? "light"} className="font-size-13">
                  {titleCase(lead.status)}
                </Badge>
              </div>

              <hr />

              <Row className="g-2">
                <Col sm={4}><small className="text-muted d-block">Source</small>{titleCase(lead.source)}</Col>
                <Col sm={4}><small className="text-muted d-block">Priority</small>{titleCase(lead.priority)}</Col>
                <Col sm={4}><small className="text-muted d-block">Assignee</small>{lead.assignee?.name ?? "—"}</Col>
                <Col sm={4}><small className="text-muted d-block">Interested in</small>{lead.interested_in ?? "—"}</Col>
                <Col sm={4}><small className="text-muted d-block">Budget</small>{lead.budget ? lead.budget.toLocaleString() : "—"}</Col>
                <Col sm={4}><small className="text-muted d-block">Next follow-up</small>{lead.next_follow_up_at ? formatUiDateTime(lead.next_follow_up_at) : "—"}</Col>
                {lead.vehicle && (
                  <Col sm={12}><small className="text-muted d-block">Stock vehicle</small>{lead.vehicle.stock_id} · {lead.vehicle.full_chassis}</Col>
                )}
                {lead.customer && (
                  <Col sm={12}>
                    <small className="text-muted d-block">Customer</small>
                    {lead.customer.name} ({lead.customer.cuid})
                  </Col>
                )}
                {lead.lost_reason && (
                  <Col sm={12}><small className="text-muted d-block">Lost reason</small>{lead.lost_reason}</Col>
                )}
              </Row>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <h5 className="mb-3">Activity</h5>

              <div className="mb-3">
                <div className="d-flex gap-2 mb-2 flex-wrap">
                  {ACTIVITY_TYPES.map(tItem => (
                    <Button
                      key={tItem}
                      size="sm"
                      color={activityType === tItem ? "primary" : "light"}
                      onClick={() => setActivityType(tItem)}
                    >
                      {titleCase(tItem)}
                    </Button>
                  ))}
                </div>
                <Input
                  type="textarea"
                  rows={2}
                  placeholder="Log a note, call outcome, visit..."
                  value={activityBody}
                  onChange={e => setActivityBody(e.target.value)}
                  className="mb-2"
                />
                <Row className="g-2 align-items-center">
                  <Col md="auto">
                    <label className="form-label mb-0 small text-muted">Set next follow-up</label>
                  </Col>
                  <Col md={4}>
                    <Input
                      type="datetime-local"
                      value={followUpAt}
                      onChange={e => setFollowUpAt(e.target.value)}
                    />
                  </Col>
                  <Col className="text-end">
                    <Button
                      color="primary"
                      size="sm"
                      onClick={runActivity}
                      disabled={activityMutation.isPending}
                    >
                      Add
                    </Button>
                  </Col>
                </Row>
              </div>

              <ul className="list-unstyled">
                {(lead.activities ?? []).map(act => (
                  <li key={act.id} className="border-start border-2 ps-3 pb-3 position-relative">
                    <div className="d-flex justify-content-between">
                      <strong className="text-capitalize">
                        {act.type === "status_change"
                          ? `Status: ${titleCase(act.from_status)} → ${titleCase(act.to_status)}`
                          : titleCase(act.type)}
                      </strong>
                      <small className="text-muted">{formatUiDateTime(act.created_at)}</small>
                    </div>
                    {act.body && <div>{act.body}</div>}
                    {act.follow_up_at && (
                      <small className="text-muted">Follow-up set: {formatUiDateTime(act.follow_up_at)}</small>
                    )}
                    <div className="text-muted small">{act.user?.name ?? "System"}</div>
                  </li>
                ))}
                {(lead.activities ?? []).length === 0 && (
                  <li className="text-muted">No activity yet.</li>
                )}
              </ul>
            </CardBody>
          </Card>
        </Col>

        <Col lg={5}>
          {canManageStatus && (
            <Card>
              <CardBody>
                <h5 className="mb-3">Move stage</h5>
                <Select
                  options={statusOptions}
                  value={statusValue}
                  onChange={setStatusValue}
                  placeholder="Choose stage"
                  className="mb-2"
                />
                {nextStatus === "lost" && (
                  <Input
                    type="textarea"
                    rows={2}
                    placeholder="Reason for losing this lead (required)"
                    value={lostReason}
                    onChange={e => setLostReason(e.target.value)}
                    className="mb-2"
                  />
                )}
                {nextStatus === "won" && !lead.customer_id && (
                  <div className="alert alert-warning py-2 small mb-2">
                    Convert the lead to a customer before marking it won.
                  </div>
                )}
                <Button
                  color="primary"
                  onClick={runStatus}
                  disabled={!statusValue || statusMutation.isPending}
                >
                  Apply
                </Button>
              </CardBody>
            </Card>
          )}

          {canAssign && (
            <Card>
              <CardBody>
                <h5 className="mb-3">Assign</h5>
                <Select
                  options={assigneeOptions}
                  value={assignValue}
                  onChange={setAssignValue}
                  placeholder="Choose salesperson"
                  className="mb-2"
                />
                <Button
                  color="primary"
                  onClick={runAssign}
                  disabled={!assignValue || assignMutation.isPending}
                >
                  Assign
                </Button>
              </CardBody>
            </Card>
          )}

          {canConvert && !lead.customer_id && (
            <Card>
              <CardBody>
                <h5 className="mb-3">Convert to customer</h5>
                <Input
                  placeholder={`Name (default: ${lead.name})`}
                  value={convertName}
                  onChange={e => setConvertName(e.target.value)}
                  className="mb-2"
                />
                <Input
                  placeholder={`Mobile (default: ${lead.mobile})`}
                  value={convertMobile}
                  onChange={e => setConvertMobile(e.target.value)}
                  className="mb-2"
                />
                <Button
                  color="success"
                  onClick={runConvert}
                  disabled={convertMutation.isPending}
                >
                  Create customer
                </Button>
              </CardBody>
            </Card>
          )}

          <Button color="light" onClick={() => navigate("/leads")}>
            <i className="mdi mdi-arrow-left" /> Back to leads
          </Button>
        </Col>
      </Row>
    </PageWrapper>
  )
}

export default LeadDetail
